<template>
    <div class="g-map-wrapper" :class="{ 'fullscreen': isFullscreen }">
      <!-- 地图区域 -->
      <div id="map-view-preview" :style="{ width: '100%', height: '100%' }" >
      </div>
      <!-- 经纬度展示 -->
      <div class="coordinate-display">
        {{ currentLatLng }}
      </div>
      <!-- 全屏按钮 -->
      <div v-if="isNeedFull" class="fullscreen-btn" @click="toggleFullscreen">
        <FullscreenOutlined v-if="!isFullscreen" style="font-size: 20px; color: #fff;" />
        <FullscreenExitOutlined v-else style="font-size: 20px; color: #fff;" />
      </div>
    </div>
  </template>

<script lang="ts" setup>
import { loadMapEnvironment } from '/@/utils/map-layer-utils'
import { currentClientConfig } from '/@/config/env'
import { computed, defineProps, onMounted, onUnmounted, reactive, ref, watch, defineEmits } from 'vue'
// coordtransform removed - using WGS-84 directly

import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons-vue'
interface Markers {
  name: string,
  marker: any
}
const props = defineProps<{
    mapInfo:any,
    isNeedFull : boolean
}>()
const aMapView = ref()
const MapViewContainer = ref()
const markers = [] as Markers[]
const infoWindow = ref()
const isFullscreen = ref(false)
const currentLatLng = ref('')
let imageMarker: any = null // 图片标记

// 切换全屏
function toggleFullscreen () {
  isFullscreen.value = !isFullscreen.value
}

// 格式化经纬度显示
function formatCoordinate (lng: number, lat: number): string {
  const latStr = lat >= 0 ? `${lat.toFixed(9)}° N` : `${Math.abs(lat).toFixed(9)}° S`
  const lngStr = lng >= 0 ? `${lng.toFixed(9)}° E` : `${lng.toFixed(9)}° W`
  return `${latStr}  ${lngStr}`
}

watch(() => props.mapInfo, (newValue, oldValue) => {
  removeMarker(oldValue.file_name)
  if (newValue.lat !== '0.0') {
    // const point = [Number(newValue.lng), Number(newValue.lat])
    // initMarker(newValue.object_keys, newValue.file_name, point[0], point[1])
    const point = [Number(newValue.lng), Number(newValue.lat)]
    initInfoWindow(newValue.object_keys, point)
  }
}, {
  deep: true
})
onMounted(() => {
  initMap('map-view-preview', 15)
})
onUnmounted(() => {
  aMapView.value = null
  MapViewContainer.value?.destroy()
  MapViewContainer.value = null
})
function initInfoWindow (content:string, position:Array<number>) {
  if (content.indexOf('.mp4') !== -1) {
    infoWindow.value.close()
    return
  }
  const AMap = aMapView.value

  // 清除之前的图片标记
  if (imageMarker) {
    MapViewContainer.value.remove(imageMarker)
    imageMarker = null
  }

  const point = [Number(position[0]), Number(position[1])]
  // 创建新的图片标记（类似 InfoWindow 效果）
  imageMarker = new AMap.Marker({
    position: new AMap.LngLat(point[0], point[1]),
    content: `<img src="${content}" width="40px" height="40px" style="border: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.3);" />`,
    offset: new AMap.Pixel(-20, -20),
    anchor: 'top-center'
  })
  MapViewContainer.value.add(imageMarker)
}
async function initMap (container: string, zoom?:number) {
  const onOperation = (AMap, layers) => {
    aMapView.value = AMap
    //  const centerPoint = CURRENT_CONFIG.mapCenter
    const point = [Number(props.mapInfo.lng), Number(props.mapInfo.lat)]
    currentLatLng.value = formatCoordinate(Number(point[0]), Number(point[1]))
    MapViewContainer.value = new AMap.Map(container, {
      center: point,
      zoom: zoom || 15,
      layers: layers,

    })
    infoWindow.value = new AMap.InfoWindow({ offset: new AMap.Pixel(0, -30) })

    if (props.mapInfo.lat !== '0.0') {
      const point = [Number(props.mapInfo.lng), Number(props.mapInfo.lat)]
      initInfoWindow(props.mapInfo.object_keys, point)
    }
  }

  loadMapEnvironment().then(({ AMap, layers }) => {
    onOperation(AMap, layers)
  }).catch(e => {
    console.log(e)
  })
}

function initMarker (url: string, name: string, lng?: number, lat?: number) {
  if (aMapView.value === undefined) {
    return
  }
  const AMap = aMapView.value
  const markerImg = new AMap.Marker({
    position: new AMap.LngLat(lng || 113.943225499, lat || 22.577673716),
    icon: initIcon('//a.amap.com/jsapi_demos/static/demo-center/icons/poi-marker-default.png'),
    title: name,
    anchor: 'top-center',
    offset: [0, -20],
  })

  markers.push({
    name: name,
    marker: markerImg
  })
  MapViewContainer.value.add(markerImg)
}
function removeMarker (name: string) {
  markers.forEach(el => {
    if (name === el.name) {
      MapViewContainer.value.remove(el.marker)
      // delete markers[sn]
    }
  })
}
function initIcon (url:string) {
  const AMap = aMapView.value
  return new AMap.Icon({
    image: url,
    imageSize: new AMap.Size(40, 40),
    size: new AMap.Size(40, 40)
  })
}

// 获得多个点位的中心点
function getPointsCenter (points:any[]) {
  const center = [0, 0]
  points.forEach((item:any) => {
    center[0] += item[0]
    center[1] += item[1]
  })
  center[0] = center[0] / points.length
  center[1] = center[1] / points.length
  return center
}
function relocateCenter (points?:any[]) {
  const center = getPointsCenter(points)
  MapViewContainer.value.setCenter(center)
}
</script>

  <style lang="scss" scoped>

  .g-map-wrapper {
    height: 100%;
    width: 100%;
    position: relative;

    &.fullscreen {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 9999;
    }

    .fullscreen-btn {
      position: absolute;
      bottom: 32px;
      right: 12px;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 4px;
      cursor: pointer;
      z-index: 10000;
      transition: background 0.3s;

      &:hover {
        background: rgba(0, 0, 0, 0.8);
      }
    }

    .coordinate-display {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 24px;
      background: rgba(0, 0, 0, 0.7);
      color: #fff;
      font-size: 14px;
      display: flex;
      align-items: center;
      z-index: 9999;
      letter-spacing: 1px;
    }
  }
  :deep(){
    .amap-info-content{
        padding: 1px;
    }
    .amap-info-close{
        display: none;
    }
  }

  </style>
